/* The phone app — a port of `Mill Mobile App.dc.html`.
 *
 * One fetch of /api/mobile/summary on open, then seven screens drawn from
 * it: home, production, stock, cotton rates, cotton purchases, sales and
 * workforce. The design paints them inside a phone frame on a canvas and
 * keeps the current screen in component state; here they are real routes
 * under /m, so the hardware back button and a shared link both work, and
 * the browser supplies the chrome the frame was standing in for.
 *
 * Two other departures from the design, both of them it being a mockup:
 *   - the iOS / Android picker and the fake status bar are gone — a real
 *     phone has its own, and the safe-area insets in mobile.css keep the
 *     header and the scroll body clear of the notch and the home bar;
 *   - sign-in goes through the real /api/auth/login rather than accepting
 *     anything, and the drawer's sign-out ends the real session.
 *
 * Owner / Manager is a view preference the design introduced and is kept:
 * it reorders the home tiles and swaps the four headline figures. It is not
 * a permission — the signed-in role still decides what the API will answer.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { type MobileSummary, mobileApi } from '../lib/api_mobile'
import { ROLE_LABEL, useAuth } from '../lib/auth'
import { useIsDark, useTheme } from '../lib/theme'
import './mobile.css'
import {
  GROUPS, PATHS, type Role, type ScreenId, TITLES, readSaved, screenOf, writeSaved,
} from './model'
import { Cross, Hamburger } from './parts'
import { Cotton } from './screens/Cotton'
import { Home } from './screens/Home'
import { Production, type ProdTab } from './screens/Production'
import { Purchases } from './screens/Purchases'
import { Sales, type SalesTab } from './screens/Sales'
import { Stock, type StockTab } from './screens/Stock'
import { Workforce } from './screens/Workforce'

export function MobileApp() {
  const { user, signOut } = useAuth()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const screen = screenOf(pathname)
  const dark = useIsDark()
  const [, setTheme] = useTheme()

  const [data, setData] = useState<MobileSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [menu, setMenu] = useState(false)
  const [role, setRole] = useState<Role>(() => readSaved().role)

  // Per-screen view state. It lives here rather than in each screen so that
  // going home and back does not reset the day you were looking at.
  const [day, setDay] = useState<number | null>(null)
  const [prodTab, setProdTab] = useState<ProdTab>('day')
  const [stockTab, setStockTab] = useState<StockTab>('yarn')
  const [salesTab, setSalesTab] = useState<SalesTab>('yarn')
  const [staple, setStaple] = useState('all')
  const [variety, setVariety] = useState<string | null>(null)

  const scroll = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    mobileApi.summary().then(
      (d) => { if (alive) { setData(d); setError(null) } },
      (e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : 'Could not load the mill data.')
      },
    )
    return () => { alive = false }
  }, [])

  // Each screen starts at the top, as a pushed screen does on a phone.
  useEffect(() => { scroll.current?.scrollTo({ top: 0 }) }, [pathname])

  const go = (id: ScreenId) => {
    setMenu(false)
    navigate(PATHS[id])
  }
  const pickRole = (r: Role) => { setRole(r); writeSaved({ role: r }) }
  const toggleTheme = () => setTheme(dark ? 'light' : 'dark')

  // The last day on file is the day the app opens on, until one is tapped.
  const lastDay = data ? Math.max(0, data.prod.days.length - 1) : 0
  const dayIndex = day == null ? lastDay : Math.min(day, lastDay)

  const title = TITLES[screen]

  return (
    <div className="m-app">
      {screen !== 'home' && (
        <div className="m-bar">
          <button type="button" className="m-plain" onClick={() => go('home')}
            style={{ justifySelf: 'start', fontSize: 16 }}>‹ Home</button>
          <h1>{title}</h1>
          <button type="button" className="m-icon-btn" onClick={() => setMenu(true)}
            title="All dashboards" aria-label="All dashboards"
            style={{ justifySelf: 'end' }}><Hamburger /></button>
        </div>
      )}

      <div ref={scroll}
        className={'m-scroll m-noscroll' + (screen === 'home' ? ' m-pad-top' : '')}>
        {screen === 'home' && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 8, paddingTop: 6,
          }}>
            <button type="button" className="m-icon-btn" onClick={() => setMenu(true)}
              title="All dashboards" aria-label="All dashboards"><Hamburger /></button>
            <div className="m-mini" style={{ marginRight: 'auto' }}>
              <button type="button" aria-pressed={role === 'owner'}
                onClick={() => pickRole('owner')}>Owner</button>
              <button type="button" aria-pressed={role === 'manager'}
                onClick={() => pickRole('manager')}>Manager</button>
            </div>
            <button type="button" className="m-btn" onClick={toggleTheme}>
              {dark ? 'Light' : 'Dark'}
            </button>
          </div>
        )}

        {error ? (
          <Message title="Can't reach the mill">{error}</Message>
        ) : !data ? (
          <Message title="Loading">One moment — fetching the month.</Message>
        ) : data.empty ? (
          <Message title="Nothing on file">
            No tracker days have been loaded yet. Load a daily tracker workbook
            on the web app's Data loads page.
          </Message>
        ) : (
          <Screen
            screen={screen} data={data} role={role} dark={dark}
            day={dayIndex} setDay={setDay}
            prodTab={prodTab} setProdTab={setProdTab}
            stockTab={stockTab} setStockTab={setStockTab}
            salesTab={salesTab} setSalesTab={setSalesTab}
            staple={staple} setStaple={setStaple}
            variety={variety} setVariety={setVariety}
            onOpen={go}
            onPickDay={(i) => { setDay(i); setProdTab('day'); go('production') }}
          />
        )}
      </div>

      {menu && (
        <>
          <button type="button" className="m-scrim" aria-label="Close menu"
            onClick={() => setMenu(false)} />
          <nav className="m-drawer">
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              gap: 8, padding: '8px 16px 14px', borderBottom: '1px solid var(--color-divider)',
            }}>
              <div>
                <div className="m-eyebrow" style={{ fontSize: 9.5 }}>Mohans Sri Chand Tex</div>
                <div style={{
                  fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 600,
                  lineHeight: 1.1, marginTop: 3,
                }}>Mill dashboards</div>
              </div>
              <button type="button" className="m-icon-btn" onClick={() => setMenu(false)}
                title="Close" aria-label="Close menu"><Cross /></button>
            </div>

            <div className="m-noscroll" style={{
              flex: 1, overflowY: 'auto', padding: '6px 0 12px',
            }}>
              {GROUPS.map((g, gi) => (
                <div key={g.name || `g${gi}`}>
                  {g.name ? (
                    <div className="m-label" style={{
                      padding: '12px 16px 4px', letterSpacing: '0.16em',
                    }}>{g.name}</div>
                  ) : null}
                  {g.items.map((id) => (
                    <button key={id} type="button" className="m-drawer-item"
                      aria-current={screen === id ? 'page' : undefined}
                      onClick={() => go(id)}>
                      <span>{TITLES[id]}</span>
                      <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>›</span>
                    </button>
                  ))}
                </div>
              ))}

              {/* The desktop portal has the pages a phone leaves out —
                  profitability, global trade, the loads and the raw tables. */}
              <div className="m-label" style={{ padding: '18px 16px 4px', letterSpacing: '0.16em' }}>
                Elsewhere
              </div>
              <Link to="/production" className="m-drawer-item"
                onClick={() => writeSaved({ desktop: true })}
                style={{ textDecoration: 'none' }}>
                <span>Full web app</span>
                <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>↗</span>
              </Link>
            </div>

            <div style={{
              flex: 'none', display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
              borderTop: '1px solid var(--color-divider)',
            }}>
              <span style={{
                flex: 'none', width: 30, height: 30, display: 'grid', placeItems: 'center',
                background: 'var(--color-accent-100)', color: 'var(--color-accent-800)',
                font: '600 13px var(--font-heading)',
              }}>{user.username.slice(0, 1).toUpperCase()}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.3 }}>
                <span className="m-ellipsis" style={{ display: 'block' }}>{user.username}</span>
                <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
                  {ROLE_LABEL[user.role]} · {role === 'owner' ? 'Owner' : 'Manager'} view
                </span>
              </span>
              {/* The home screen has its own theme button; this is the one
                  every other screen reaches. */}
              <button type="button" className="m-btn" onClick={toggleTheme}
                style={{ padding: '6px 10px', minHeight: 34 }}>{dark ? 'Light' : 'Dark'}</button>
              <button type="button" className="m-plain" onClick={signOut}
                style={{ minHeight: 36 }}>Sign out</button>
            </div>
          </nav>
        </>
      )}
    </div>
  )
}

/** Which screen the route is on. Split out so the shell above stays about
 *  chrome and this stays about dispatch. */
function Screen(p: {
  screen: ScreenId
  data: MobileSummary
  role: Role
  dark: boolean
  day: number
  setDay: (i: number) => void
  prodTab: ProdTab
  setProdTab: (t: ProdTab) => void
  stockTab: StockTab
  setStockTab: (t: StockTab) => void
  salesTab: SalesTab
  setSalesTab: (t: SalesTab) => void
  staple: string
  setStaple: (s: string) => void
  variety: string | null
  setVariety: (k: string) => void
  onOpen: (id: ScreenId) => void
  onPickDay: (i: number) => void
}) {
  switch (p.screen) {
    case 'production':
      return (
        <Production data={p.data} dark={p.dark} tab={p.prodTab} onTab={p.setProdTab}
          day={p.day} onDay={p.setDay} />
      )
    case 'stock':
      return <Stock data={p.data} tab={p.stockTab} onTab={p.setStockTab} />
    case 'cotton':
      return (
        <Cotton data={p.data} staple={p.staple} onStaple={p.setStaple}
          pick={p.variety} onPick={p.setVariety} />
      )
    case 'purchases':
      return <Purchases data={p.data} />
    case 'sales':
      return <Sales data={p.data} tab={p.salesTab} onTab={p.setSalesTab} />
    case 'workforce':
      return <Workforce data={p.data} />
    default:
      return (
        <Home data={p.data} role={p.role} dark={p.dark}
          onOpen={p.onOpen} onPickDay={p.onPickDay} />
      )
  }
}

function Message({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div className="m-eyebrow">Mohans Sri Chand Tex</div>
      <h1 style={{ fontSize: 30, lineHeight: 1.05, margin: '8px 0 10px' }}>{title}</h1>
      <p className="m-sub" style={{ margin: 0, lineHeight: 1.5 }}>{children}</p>
    </div>
  )
}
